import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import FeaturePermissionsGrid from "@/components/FeaturePermissionsGrid";
import { MODULES, MODULE_LABELS, effectiveModules, SPECIAL_PERMS, SPECIAL_PERM_LABELS, hasSpecialPermission } from "@/lib/permissions";
import { toast } from "sonner";
import { ShieldCheck, UserCircle, SquaresFour, Shield } from "@phosphor-icons/react";

export default function Permissions() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(null);
  const [viewMode, setViewMode] = useState("person"); // person | permission | feature
  const [activeTab, setActiveTab] = useState(null);

  const load = () => api.get("/users").then((r) => {
    setUsers(r.data);
    setActiveTab((prev) => prev || (r.data.length ? r.data[0].id : null));
  });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (u, mod) => {
    const current = new Set(effectiveModules(u));
    current.has(mod) ? current.delete(mod) : current.add(mod);
    const perms = MODULES.filter((m) => current.has(m));
    setSaving(u.id + mod);
    try {
      await api.put(`/users/${u.id}`, { permissions: perms });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, permissions: perms } : x)));
      toast.success(`Updated ${u.name}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(null); }
  };

  const toggleSpecial = async (u, key) => {
    const current = new Set(Array.isArray(u.special_permissions) ? u.special_permissions : []);
    current.has(key) ? current.delete(key) : current.add(key);
    const special = SPECIAL_PERMS.filter((k) => current.has(k));
    setSaving(u.id + key);
    try {
      await api.put(`/users/${u.id}`, { special_permissions: special });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, special_permissions: special } : x)));
      toast.success(`Updated ${u.name}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(null); }
  };

  const filteredUsers = users.filter((u) => u.name.toLowerCase().includes(search.toLowerCase()) || (u.email || "").toLowerCase().includes(search.toLowerCase()));

  const permTabs = [
    ...MODULES.map((m) => ({ key: m, label: MODULE_LABELS[m], kind: "module" })),
    ...SPECIAL_PERMS.map((k) => ({ key: k, label: SPECIAL_PERM_LABELS[k], kind: "special" })),
  ];
  const activePermTab = viewMode === "permission" ? permTabs.find((t) => t.key === activeTab) : null;
  const activeUser = viewMode === "person" ? users.find((u) => u.id === activeTab) : null;

  const switchMode = (mode) => {
    setViewMode(mode);
    if (mode === "feature") {
      setActiveTab(null);
      return;
    }
    setActiveTab(mode === "person" ? (filteredUsers[0]?.id || users[0]?.id || null) : permTabs[0].key);
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] animate-fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-1">Administration</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-zinc-50 flex items-center gap-2"><ShieldCheck size={28} className="text-emerald-400" /> Permissions</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">Delegate access per person. Admins always have full access. Changes save instantly.</p>
        </div>
        <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-sm p-1" data-testid="perm-view-toggle">
          <button
            onClick={() => switchMode("person")}
            data-testid="perm-view-person"
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-sm transition-colors ${viewMode === "person" ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-100"}`}
          >
            <UserCircle size={15} /> By Person
          </button>
          <button
            onClick={() => switchMode("permission")}
            data-testid="perm-view-permission"
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-sm transition-colors ${viewMode === "permission" ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-100"}`}
          >
            <SquaresFour size={15} /> By Permission
          </button>
          <button
            onClick={() => switchMode("feature")}
            data-testid="perm-view-feature"
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-sm transition-colors ${viewMode === "feature" ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-100"}`}
          >
            <Shield size={15} /> Feature Permissions
          </button>
        </div>
      </div>

      {viewMode === "person" && (
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search people…" className="bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 w-56 mb-4" data-testid="perm-search" />
      )}

      {/* Tab bar — one tab per team member (viewMode="person") or one tab per permission (viewMode="permission") */}
      {viewMode !== "feature" && <div className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap border-b border-zinc-800 pb-2 mb-5" data-testid="perm-tab-bar">
        {viewMode === "person"
          ? filteredUsers.map((u) => (
              <button
                key={u.id}
                onClick={() => setActiveTab(u.id)}
                data-testid={`perm-tab-person-${u.id}`}
                className={`shrink-0 px-3.5 py-2 rounded-sm text-sm font-medium border transition-colors ${activeTab === u.id ? "bg-emerald-600/10 border-emerald-600 text-emerald-400" : "border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"}`}
              >
                {u.name}
                <span className="ml-1.5 text-[10px] text-zinc-500 capitalize">· {u.role}</span>
              </button>
            ))
          : permTabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-testid={`perm-tab-permission-${t.key}`}
                className={`shrink-0 px-3.5 py-2 rounded-sm text-sm font-medium border transition-colors ${activeTab === t.key ? (t.kind === "special" ? "bg-emerald-500/10 border-emerald-500 text-emerald-400" : "bg-emerald-600/10 border-emerald-600 text-emerald-400") : "border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"}`}
              >
                {t.label}
              </button>
            ))}
        {viewMode === "person" && filteredUsers.length === 0 && <span className="text-sm text-zinc-500 px-2">No members found.</span>}
      </div>}

      {viewMode === "feature" && <FeaturePermissionsGrid />}

      {/* ---- By Person: full checklist for the selected team member ---- */}
      {viewMode === "person" && activeUser && (
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-sm p-5 max-w-2xl" data-testid="perm-person-panel">
          <div className="mb-4">
            <div className="text-zinc-100 text-lg font-semibold">{activeUser.name}</div>
            <div className="text-xs text-zinc-500 capitalize">{activeUser.email} · {activeUser.role}{activeUser.role === "admin" && " · full access, can't be changed"}</div>
          </div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Tabs / Modules</div>
          <div className="grid grid-cols-2 gap-2 mb-5">
            {MODULES.map((m) => {
              const eff = effectiveModules(activeUser);
              const isAdmin = activeUser.role === "admin";
              return (
                <label key={m} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer py-1" data-testid={`perm-person-module-${m}`}>
                  <Checkbox
                    checked={isAdmin || eff.includes(m)}
                    disabled={isAdmin || saving === activeUser.id + m}
                    onCheckedChange={() => toggle(activeUser, m)}
                    className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600 disabled:opacity-50"
                  />
                  {MODULE_LABELS[m]}
                </label>
              );
            })}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-500 mb-2">Granular Permissions</div>
          <div className="grid grid-cols-1 gap-2">
            {SPECIAL_PERMS.map((k) => {
              const isAdmin = activeUser.role === "admin";
              return (
                <label key={k} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer py-1" data-testid={`perm-person-special-${k}`}>
                  <Checkbox
                    checked={hasSpecialPermission(activeUser, k)}
                    disabled={isAdmin || saving === activeUser.id + k}
                    onCheckedChange={() => toggleSpecial(activeUser, k)}
                    className="border-zinc-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500 disabled:opacity-50"
                  />
                  {SPECIAL_PERM_LABELS[k]}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* ---- By Permission: checklist of every team member for the selected permission ---- */}
      {viewMode === "permission" && activePermTab && (
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-sm max-w-2xl" data-testid="perm-permission-panel">
          <div className="px-5 py-4 border-b border-zinc-800">
            <div className="text-zinc-100 text-lg font-semibold">{activePermTab.label}</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              {activePermTab.kind === "module" ? "Grants access to this tab/section." : "A granular action-level permission (not tied to a whole tab)."}
            </div>
          </div>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search people…" className="bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 m-4 mb-0 w-56" data-testid="perm-search-in-tab" />
          <div className="divide-y divide-zinc-800/60 mt-2">
            {filteredUsers.map((u) => {
              const isAdmin = u.role === "admin";
              const checked = activePermTab.kind === "module" ? (isAdmin || effectiveModules(u).includes(activePermTab.key)) : hasSpecialPermission(u, activePermTab.key);
              return (
                <label key={u.id} className="flex items-center gap-3 px-5 py-2.5 text-sm text-zinc-300 cursor-pointer hover:bg-zinc-900/50" data-testid={`perm-${u.id}-${activePermTab.key}`}>
                  <Checkbox
                    checked={checked}
                    disabled={isAdmin || saving === u.id + activePermTab.key}
                    onCheckedChange={() => (activePermTab.kind === "module" ? toggle(u, activePermTab.key) : toggleSpecial(u, activePermTab.key))}
                    className={`border-zinc-600 disabled:opacity-50 ${activePermTab.kind === "module" ? "data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" : "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"}`}
                  />
                  <span className="flex-1">{u.name}</span>
                  <span className="text-[11px] text-zinc-500 capitalize">{u.role}{isAdmin && " · full access"}</span>
                </label>
              );
            })}
            {filteredUsers.length === 0 && <div className="px-5 py-6 text-sm text-zinc-500">No members found.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
